import { useEffect, useState, useRef } from "react";
import api, { apiError } from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, rupee } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Textarea } from "../components/ui/textarea";
import { Plus, Pencil, Copy, Power, Search, Upload, Trash2, ImageOff, Gift, X } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (p) => (p ? `${BACKEND}${p}` : "");


const EMPTY = {
  name: "", category: "", sub_category: "", price: 0, actual_price: 0, duration: 30, gender: "unisex",
  tax: 0, description: "", branch_ids: [], branch_prices: {}, status: "active", incentive_eligible: true,
  online_booking: true, editable_price: false, image: "", instagram_video_url: "", video_url: "",
};

export default function Services() {
  const { branches } = useBranch();
  const { user } = useAuth();
  const isOwner = user?.role === "OWNER";
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [catOpen, setCatOpen] = useState(false);
  const [newCat, setNewCat] = useState("");
  const catFileRef = useRef(null);
  const svcFileRef = useRef(null);
  const [svcUploading, setSvcUploading] = useState(false);
  const [catUploadId, setCatUploadId] = useState(null);
  const [fpOpen, setFpOpen] = useState(false);
  const [fp, setFp] = useState({ enabled: false, service_ids: [], slabs: [] });
  const [fpQ, setFpQ] = useState("");
  const [confirmState, setConfirmState] = useState(null);
  const [editCatId, setEditCatId] = useState(null);
  const [editCatName, setEditCatName] = useState("");
  const askConfirm = (title, message, onConfirm) => setConfirmState({ title, message, onConfirm });
  const loadFp = () => api.get("/freedom-package").then((r) => setFp({ enabled: !!r.data.enabled, service_ids: r.data.service_ids || [], slabs: r.data.slabs || [] })).catch(() => {});
  const saveFp = async () => {
    try {
      await api.put("/freedom-package", { enabled: fp.enabled, service_ids: fp.service_ids, slabs: fp.slabs.map((s) => ({ count: Number(s.count), price: Number(s.price) })) });
      toast.success("Freedom Package saved"); setFpOpen(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggleFpSvc = (id) => setFp((f) => ({ ...f, service_ids: f.service_ids.includes(id) ? f.service_ids.filter((x) => x !== id) : [...f.service_ids, id] }));
  const setSlab = (i, patch) => setFp((f) => { const s = [...f.slabs]; s[i] = { ...s[i], ...patch }; return { ...f, slabs: s }; });
  const addSlab = () => setFp((f) => ({ ...f, slabs: [...f.slabs, { count: (f.slabs.length ? Math.max(...f.slabs.map((x) => Number(x.count))) + 1 : 2), price: 0 }] }));
  const delSlab = (i) => setFp((f) => ({ ...f, slabs: f.slabs.filter((_, k) => k !== i) }));

  const onCatFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file || !catUploadId) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/service-categories/${catUploadId}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Category image updated"); loadCats(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setCatUploadId(null); }
  };
  const removeCatImage = async (c) => {
    try { await api.delete(`/service-categories/${c.id}/image`); toast.success("Image removed"); loadCats(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const onServiceFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setSvcUploading(true);
    try {
      const { data } = await api.post("/services/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.image }));
      toast.success("Service image uploaded");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Image upload failed"); }
    finally { setSvcUploading(false); }
  };

  const loadServices = () => api.get("/services").then((r) => setServices(r.data));
  const loadCats = () => api.get("/service-categories").then((r) => setCategories(r.data));
  useEffect(() => { loadServices(); loadCats(); loadFp(); }, []);

  const payload = () => ({
    ...form, price: Number(form.price), actual_price: Number(form.actual_price || 0), duration: Number(form.duration), tax: Number(form.tax),
    branch_prices: Object.fromEntries(Object.entries(form.branch_prices || {}).filter(([, v]) => v !== "" && v != null).map(([k, v]) => [k, Number(v)])),
  });

  const save = async () => {
    if (!form.name || !form.category) { toast.error("Service name and category are required"); return; }
    try {
      if (editId) await api.put(`/services/${editId}`, payload());
      else await api.post("/services", payload());
      toast.success("Service saved"); setOpen(false); loadServices();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const duplicate = async (s) => {
    const { id, ...rest } = s;
    try { await api.post("/services", { ...EMPTY, ...rest, name: `${s.name} (Copy)` }); toast.success("Duplicated"); loadServices(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleStatus = async (s) => {
    const next = s.status === "inactive" ? "active" : "inactive";
    try { await api.put(`/services/${s.id}`, { ...EMPTY, ...s, status: next }); toast.success(next === "active" ? "Activated" : "Deactivated"); loadServices(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const deleteService = (s) => askConfirm("Delete Service", `Are you sure you want to delete "${s.name}"? This cannot be undone.`, async () => {
    try { await api.delete(`/services/${s.id}`); toast.success("Service deleted"); loadServices(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  });
  const startEditCat = (c) => { setEditCatId(c.id); setEditCatName(c.name); };
  const saveEditCat = async (c) => {
    const name = editCatName.trim();
    if (!name) { toast.error("Category name required"); return; }
    try { await api.put(`/service-categories/${c.id}`, { name }); toast.success("Category updated"); setEditCatId(null); loadCats(); loadServices(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const deleteCat = (c) => {
    const count = services.filter((s) => s.category === c.name).length;
    askConfirm("Delete Category",
      count ? `"${c.name}" has ${count} service${count > 1 ? "s" : ""}. Deleting the category will NOT delete those services, but they will no longer be grouped under it. Continue?` : `Delete category "${c.name}"?`,
      async () => { try { await api.delete(`/service-categories/${c.id}`); toast.success("Category deleted"); loadCats(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } });
  };

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try { await api.post("/service-categories", { name: newCat.trim() }); setNewCat(""); setCatOpen(false); toast.success("Category added"); loadCats(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openEdit = (s) => { setForm({ ...EMPTY, ...s, branch_prices: s.branch_prices || {} }); setEditId(s.id); setOpen(true); };
  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };

  const toggleBranch = (bid) => setForm((f) => ({ ...f, branch_ids: f.branch_ids.includes(bid) ? f.branch_ids.filter((x) => x !== bid) : [...f.branch_ids, bid] }));
  const setBranchPrice = (bid, v) => setForm((f) => ({ ...f, branch_prices: { ...f.branch_prices, [bid]: v } }));

  const filtered = services.filter((s) => {
    const t = q.toLowerCase();
    return !t || (s.name || "").toLowerCase().includes(t) || (s.category || "").toLowerCase().includes(t) || (s.sub_category || "").toLowerCase().includes(t);
  });

  return (
    <div>
      <PageHeader title="Service Management" subtitle="Add unlimited services & categories — instantly available in billing"
        actions={
          <div className="flex gap-2">
            {isOwner && <Button variant="outline" onClick={() => setCatOpen(true)} data-testid="add-category-btn" className="rounded-sm"><Plus size={16} className="mr-1" /> Category</Button>}
            {isOwner && <Button variant="outline" onClick={() => setFpOpen(true)} data-testid="freedom-package-btn" className="rounded-sm border-primary/50 text-primary"><Gift size={16} className="mr-1" /> Freedom Package</Button>}
            {isOwner && <Button onClick={openNew} data-testid="add-service-btn" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]"><Plus size={16} className="mr-1" /> Add Service</Button>}
          </div>
        } />

      <div className="relative mb-4 max-w-md">
        <Search size={16} className="absolute left-3 top-3 text-muted-foreground" />
        <Input placeholder="Search services…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="service-list-search" className="pl-9 bg-secondary" />
      </div>

      <Card className="bg-card border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Service</TableHead><TableHead>Category</TableHead>
              <TableHead>Gender</TableHead><TableHead className="text-right">Duration</TableHead>
              <TableHead className="text-right">Actual</TableHead><TableHead className="text-right">Offer</TableHead><TableHead>Branch</TableHead>
              <TableHead>Status</TableHead><TableHead>Online</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((s) => (
              <TableRow key={s.id} className="border-border" data-testid={`service-row-${s.id}`}>
                <TableCell className="font-medium">{s.name}{s.sub_category && <span className="text-xs text-muted-foreground block">{s.sub_category}</span>}</TableCell>
                <TableCell className="text-muted-foreground">{s.category}</TableCell>
                <TableCell className="capitalize">{s.gender}</TableCell>
                <TableCell className="text-right">{s.duration} min</TableCell>
                <TableCell className="text-right text-muted-foreground line-through">{s.actual_price ? rupee(s.actual_price) : "—"}</TableCell>
                <TableCell className="text-right text-primary font-medium">{rupee(s.price)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{s.branch_ids?.length ? `${s.branch_ids.length} branch` : "All"}</TableCell>
                <TableCell><Badge variant="outline" className={s.status === "inactive" ? "text-muted-foreground" : "text-green-300 border-green-800"}>{s.status === "inactive" ? "Inactive" : "Active"}</Badge></TableCell>
                <TableCell data-testid={`service-online-${s.id}`}><Badge variant="outline" className={s.online_booking === false ? "text-muted-foreground" : "text-blue-300 border-blue-800"}>{s.online_booking === false ? "No" : "Yes"}</Badge></TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {isOwner && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(s)} data-testid={`edit-service-${s.id}`}><Pencil size={14} /></Button>}
                    {isOwner && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => duplicate(s)} data-testid={`dup-service-${s.id}`}><Copy size={14} /></Button>}
                    {isOwner && <Button size="icon" variant="ghost" className={`h-8 w-8 ${s.status === "inactive" ? "text-green-400" : "text-orange-300"}`} onClick={() => toggleStatus(s)} data-testid={`toggle-service-${s.id}`}><Power size={14} /></Button>}
                    {isOwner && <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => deleteService(s)} data-testid={`delete-service-${s.id}`}><Trash2 size={14} /></Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!filtered.length && <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-10">No services.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      {/* add/edit service */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit" : "Add"} Service</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Service Name *</Label><Input data-testid="service-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-secondary mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Category *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="bg-secondary mt-1" data-testid="service-category"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Sub Category</Label><Input data-testid="service-subcategory" value={form.sub_category} onChange={(e) => setForm({ ...form, sub_category: e.target.value })} className="bg-secondary mt-1" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Gender</Label>
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger className="bg-secondary mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{["unisex", "male", "female"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Duration (min)</Label><Input type="number" data-testid="service-duration" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="bg-secondary mt-1" /></div>
              <div><Label className="text-xs">Tax %</Label><Input type="number" value={form.tax} onChange={(e) => setForm({ ...form, tax: e.target.value })} className="bg-secondary mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Actual Price (₹)</Label><Input type="number" data-testid="service-actual-price" value={form.actual_price} onChange={(e) => setForm({ ...form, actual_price: e.target.value })} className="bg-secondary mt-1" /></div>
              <div><Label className="text-xs">Offer Price (₹) *</Label><Input type="number" data-testid="service-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-secondary mt-1" /></div>
            </div>
            <p className="text-[11px] text-muted-foreground -mt-1">Offer Price is charged to the customer (GST-inclusive). Actual Price shows the saving on the invoice.</p>
            <div>
              <Label className="text-xs">Branch Availability <span className="text-muted-foreground">(none = all branches)</span></Label>
              <div className="space-y-2 mt-1.5">
                {branches.map((b) => (
                  <div key={b.id} className="flex items-center gap-2">
                    <button type="button" onClick={() => toggleBranch(b.id)} data-testid={`service-branch-${b.id}`}
                      className={`text-xs px-3 py-1 rounded-full border flex-1 text-left ${form.branch_ids.includes(b.id) ? "bg-primary text-primary-foreground border-primary" : "bg-secondary text-muted-foreground border-border"}`}>{b.name}</button>
                    {form.branch_ids.includes(b.id) && (
                      <Input type="number" placeholder="Branch price" value={form.branch_prices?.[b.id] ?? ""} onChange={(e) => setBranchPrice(b.id, e.target.value)} className="w-28 h-8 bg-secondary" data-testid={`service-branch-price-${b.id}`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div><Label className="text-xs">Description <span className="text-muted-foreground">(shown on the public booking card)</span></Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Write a short description for this specific service…" className="bg-secondary mt-1" data-testid="service-description" /></div>
            <div>
              <Label className="text-xs">Service Image <span className="text-muted-foreground">(shown on the public booking card)</span></Label>
              <div className="flex items-center gap-3 mt-1">
                {form.image
                  ? <img src={imgUrl(form.image)} alt="Service" className="h-16 w-16 rounded-md object-cover border border-border" data-testid="service-image-preview" />
                  : <div className="h-16 w-16 rounded-md bg-secondary border border-border flex items-center justify-center text-muted-foreground"><ImageOff size={18} /></div>}
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" variant="outline" className="rounded-sm" disabled={svcUploading} onClick={() => svcFileRef.current?.click()} data-testid="service-image-upload">
                    <Upload size={14} className="mr-1" />{svcUploading ? "Uploading…" : (form.image ? "Replace" : "Upload")}
                  </Button>
                  {form.image && <Button type="button" size="sm" variant="ghost" className="rounded-sm text-destructive" onClick={() => setForm({ ...form, image: "" })} data-testid="service-image-remove"><Trash2 size={14} className="mr-1" />Remove</Button>}
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">JPG, JPEG, PNG or WEBP · up to 5 MB. Leave empty to keep the card without a photo.</p>
              <input ref={svcFileRef} type="file" accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" hidden onChange={onServiceFile} data-testid="service-file-input" />
            </div>
            <div>
              <Label className="text-xs">Instagram Video / Reel Link <span className="text-muted-foreground">(optional — sent when a customer asks for this service's video)</span></Label>
              <Input value={form.instagram_video_url || ""} onChange={(e) => setForm({ ...form, instagram_video_url: e.target.value })} placeholder="https://www.instagram.com/reel/..." className="bg-secondary mt-1" data-testid="service-instagram-url" />
            </div>
            <div>
              <Label className="text-xs">Video Link <span className="text-muted-foreground">(optional — shows a "View Video" button beside Add To Cart on the online booking menu)</span></Label>
              <Input value={form.video_url || ""} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://youtube.com/shorts/... or https://.../video.mp4" className="bg-secondary mt-1" data-testid="service-video-url" />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => setForm({ ...form, incentive_eligible: !form.incentive_eligible })}
                className={`text-xs px-3 py-1 rounded-full border ${form.incentive_eligible ? "bg-primary text-primary-foreground border-primary" : "bg-secondary text-muted-foreground border-border"}`}>Incentive Eligible</button>
              <button type="button" data-testid="service-online-toggle" onClick={() => setForm({ ...form, online_booking: !form.online_booking })}
                className={`text-xs px-3 py-1 rounded-full border ${form.online_booking ? "bg-primary text-primary-foreground border-primary" : "bg-secondary text-muted-foreground border-border"}`}>Available for Online Booking: {form.online_booking ? "ON" : "OFF"}</button>
              <button type="button" data-testid="service-editable-toggle" onClick={() => setForm({ ...form, editable_price: !form.editable_price })}
                className={`text-xs px-3 py-1 rounded-full border ${form.editable_price ? "bg-primary text-primary-foreground border-primary" : "bg-secondary text-muted-foreground border-border"}`}>Editable Amount at Billing: {form.editable_price ? "ON" : "OFF"}</button>
            </div>
            <p className="text-[11px] text-muted-foreground -mt-1">Online Booking OFF keeps the service in Billing/POS only. Editable Amount lets the biller change the price on the invoice (e.g. Freedom Package, Service Add-On).</p>
          </div>
          <DialogFooter><Button onClick={save} data-testid="service-save" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">Save Service</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* manage categories */}
      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">Manage Categories</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Upload a photo for each category — it appears on your public booking site.</p>
          <div className="space-y-2 my-3">
            {categories.map((c) => (
              <div key={c.id} className="flex items-center gap-3 p-2 rounded-md border border-border bg-secondary/40" data-testid={`cat-row-${c.id}`}>
                {c.image
                  ? <img src={imgUrl(c.image)} alt={c.name} className="h-12 w-12 rounded-md object-cover border border-border" data-testid={`cat-thumb-${c.id}`} />
                  : <div className="h-12 w-12 rounded-md bg-secondary border border-border flex items-center justify-center text-muted-foreground"><ImageOff size={16} /></div>}
                {editCatId === c.id
                  ? <Input value={editCatName} onChange={(e) => setEditCatName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveEditCat(c)} autoFocus className="flex-1 h-8 bg-secondary" data-testid={`cat-edit-input-${c.id}`} />
                  : <span className="flex-1 font-medium">{c.name}</span>}
                {editCatId === c.id ? (
                  <>
                    <Button size="sm" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" onClick={() => saveEditCat(c)} data-testid={`cat-edit-save-${c.id}`}>Save</Button>
                    <Button size="sm" variant="ghost" className="rounded-sm" onClick={() => setEditCatId(null)} data-testid={`cat-edit-cancel-${c.id}`}>Cancel</Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="outline" className="rounded-sm" onClick={() => { setCatUploadId(c.id); catFileRef.current?.click(); }} data-testid={`cat-upload-${c.id}`}><Upload size={14} className="mr-1" />{c.image ? "Replace" : "Upload"}</Button>
                    {c.image && <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => removeCatImage(c)} data-testid={`cat-remove-img-${c.id}`}><ImageOff size={14} /></Button>}
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => startEditCat(c)} data-testid={`cat-rename-${c.id}`}><Pencil size={14} /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => deleteCat(c)} data-testid={`cat-delete-${c.id}`}><Trash2 size={14} /></Button>
                  </>
                )}
              </div>
            ))}
            {!categories.length && <p className="text-sm text-muted-foreground">No categories yet.</p>}
          </div>
          <input ref={catFileRef} type="file" accept="image/*" hidden onChange={onCatFile} data-testid="cat-file-input" />
          <Label className="text-xs">Add a new category</Label>
          <Input placeholder="New category name" value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCategory()} className="bg-secondary mt-1" data-testid="category-name" />
          <DialogFooter><Button onClick={addCategory} data-testid="category-save" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">Add Category</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Freedom Package config */}
      <Dialog open={fpOpen} onOpenChange={setFpOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="freedom-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Gift size={20} className="text-primary" /> Freedom Package</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Pick the eligible services, then set the fixed package price for each service-count slab. Customers pick any services from this list up to the slab count and pay the fixed slab price.</p>
          <div className="flex items-center justify-between rounded-md border border-border bg-secondary/40 px-3 py-2 my-3">
            <div className="pr-3">
              <span className="text-sm">Public Booking Status</span>
              <p className="text-xs text-muted-foreground mt-0.5">LIVE = shows on the public booking page. NOT LIVE = hidden from public booking (Billing/POS keeps working).</p>
            </div>
            <button type="button" data-testid="freedom-live-toggle" onClick={() => setFp((f) => ({ ...f, enabled: !f.enabled }))}
              className={`shrink-0 flex items-center gap-1.5 text-xs font-semibold px-4 py-1.5 rounded-full border transition-colors ${fp.enabled ? "bg-green-500/15 text-green-400 border-green-500/50" : "bg-secondary text-muted-foreground border-border"}`}>
              <span className={`h-2 w-2 rounded-full ${fp.enabled ? "bg-green-400" : "bg-muted-foreground/60"}`} />
              {fp.enabled ? "LIVE" : "NOT LIVE"}
            </button>
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs">Price Slabs (service count → fixed total price)</Label>
              <Button size="sm" variant="outline" className="rounded-sm h-7" onClick={addSlab} data-testid="freedom-add-slab"><Plus size={13} className="mr-1" /> Add Slab</Button>
            </div>
            <div className="space-y-2">
              {fp.slabs.map((s, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`freedom-slab-${i}`}>
                  <span className="text-xs text-muted-foreground">Any</span>
                  <Input type="number" value={s.count} onChange={(e) => setSlab(i, { count: e.target.value })} className="w-20 h-9 bg-secondary" data-testid={`freedom-slab-count-${i}`} />
                  <span className="text-xs text-muted-foreground">services =</span>
                  <div className="relative flex-1 max-w-[160px]"><span className="absolute left-2 top-2 text-muted-foreground">₹</span>
                    <Input type="number" value={s.price} onChange={(e) => setSlab(i, { price: e.target.value })} className="pl-6 h-9 bg-secondary" data-testid={`freedom-slab-price-${i}`} /></div>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => delSlab(i)} data-testid={`freedom-slab-del-${i}`}><Trash2 size={14} /></Button>
                </div>
              ))}
              {!fp.slabs.length && <p className="text-sm text-muted-foreground">No slabs yet. Add one, e.g. Any 2 services = ₹1399.</p>}
            </div>
          </div>

          <div>
            <Label className="text-xs">Eligible Services <span className="text-muted-foreground">({fp.service_ids.length} selected)</span></Label>
            <div className="relative my-2"><Search size={15} className="absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input value={fpQ} onChange={(e) => setFpQ(e.target.value)} placeholder="Search services…" className="pl-8 h-9 bg-secondary" data-testid="freedom-service-search" /></div>
            <div className="max-h-64 overflow-y-auto border border-border rounded-md divide-y divide-border" data-testid="freedom-service-list">
              {services.filter((s) => s.status !== "inactive").filter((s) => { const t = fpQ.toLowerCase(); return !t || (s.name || "").toLowerCase().includes(t) || (s.category || "").toLowerCase().includes(t); }).map((s) => (
                <label key={s.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-secondary/50" data-testid={`freedom-svc-${s.id}`}>
                  <input type="checkbox" checked={fp.service_ids.includes(s.id)} onChange={() => toggleFpSvc(s.id)} className="h-4 w-4 accent-[#C9A24B]" data-testid={`freedom-svc-toggle-${s.id}`} />
                  <span className="flex-1 text-sm">{s.name} <span className="text-xs text-muted-foreground">· {s.category}</span></span>
                  <span className="text-sm text-primary">{rupee(s.price)}</span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter><Button onClick={saveFp} data-testid="freedom-save" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">Save Freedom Package</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmState} onOpenChange={(o) => !o && setConfirmState(null)}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="confirm-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">{confirmState?.title}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{confirmState?.message}</p>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setConfirmState(null)} data-testid="confirm-cancel">Cancel</Button>
            <Button className="rounded-sm bg-destructive text-white hover:bg-destructive/90" data-testid="confirm-ok"
              onClick={async () => { const fn = confirmState?.onConfirm; setConfirmState(null); if (fn) await fn(); }}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
